import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { RoleGuideBanner } from '../components/RoleGuideBanner';
import PropertyCard from '../components/PropertyCard';
import ProfessionalAdvisor from '../components/ProfessionalAdvisor';
import DealTracker from '../components/DealTracker';
import BuyerConsultations from '../components/BuyerConsultations';
import AgentChoicePanel from '../components/AgentChoicePanel';
import FindProfessionals from '../components/FindProfessionals';
import DashboardTabs from '../components/DashboardTabs';
import { useCountry } from '../contexts/CountryContext';
import { useAuth } from '../contexts/AuthContext';
import BuyerJourney from '../components/BuyerJourney';
import AgentMatcher from '../components/AgentMatcher';
import GovtLinksPanel from '../components/GovtLinksPanel';
import GettingStartedGuide from '../components/GettingStartedGuide';
import RecoveryDiscountBanner from '../components/RecoveryDiscountBanner';
import LatestBlogStrip from '../components/LatestBlogStrip';
import AffiliateWidget from '../components/AffiliateWidget';
import MarketTrends from '../components/MarketTrends';
import InviteTeaserCard from '../components/InviteTeaserCard';
import FindProfessionalPromo from '../components/FindProfessionalPromo';
import BuyerPulseSubscribeCard from '../components/BuyerPulseSubscribeCard';
import EnableMatchPushButton from '../components/EnableMatchPushButton';
import LocalProsPanel from '../components/LocalProsPanel';
import SubscriptionsAndFeesCard from '../components/SubscriptionsAndFeesCard';
import ComplianceBanner from '../components/ComplianceBanner';
import { SavedSearchesTab } from '../components/SavedSearchesTab';
import { useQueryTab } from '../hooks/useQueryTab';
import { Button } from '../components/ui/button';
import { Calendar as CalendarIcon, MapPin, Clock, Shield, FileCheck, AlertCircle, CheckCircle2, Upload, Briefcase, Home, FileText, MessageSquare, Users, ClipboardCheck, HelpCircle, Bell, Activity, ArrowRight, Search as SearchIcon, TrendingUp } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const BuyerDashboard = () => {
  const { country } = useCountry();
  const { user } = useAuth();
  const isTenant = user?.role === 'tenant';
  const isInvestor = user?.role === 'investor' || user?.is_nri;
  const [bookings, setBookings] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [verification, setVerification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [activeTab, setActiveTab] = useQueryTab('portfolio', ['portfolio', 'saved-searches', 'deals', 'consultations', 'journey', 'find-pros', 'professional', 'find-agent', 'govt-links', 'agent-choice']);
  const [verifyForm, setVerifyForm] = useState({
    id_type: '',
    id_number: '',
    provide_later: false
  });
  const [verifyLoading, setVerifyLoading] = useState(false);

  const buyerTabs = useMemo(() => [
    { id: 'portfolio', label: isTenant ? 'My Saved Rentals' : 'My Properties', shortLabel: isTenant ? 'Rentals' : 'Properties', icon: <Home className="h-4 w-4" />, testId: 'tab-portfolio' },
    { id: 'saved-searches', label: 'Saved Searches', shortLabel: 'Alerts', icon: <Bell className="h-4 w-4" />, testId: 'tab-saved-searches' },
    { id: 'deals', label: isTenant ? 'My Tenancy Deals' : 'My Deals', shortLabel: isTenant ? 'Tenancies' : 'Deals', icon: <FileText className="h-4 w-4" />, testId: 'tab-deals-buyer' },
    { id: 'consultations', label: 'Messages & Advice', shortLabel: 'Messages', icon: <MessageSquare className="h-4 w-4" />, testId: 'tab-consultations-buyer' },
    { id: 'journey', label: isTenant ? 'Renting Guide' : 'Step-by-Step Guide', shortLabel: 'Guide', icon: <ClipboardCheck className="h-4 w-4" />, testId: 'tab-journey-buyer' },
    { id: 'find-pros', label: isTenant ? 'Find a Letting Agent' : 'Find a Solicitor', shortLabel: isTenant ? 'Agents' : 'Solicitors', icon: <Briefcase className="h-4 w-4" />, testId: 'tab-find-pros-buyer' },
  ], [isTenant]);


  const fetchData = useCallback(async () => {
    try {
      const [bookingsRes, favoritesRes, verificationRes] = await Promise.all([
        axios.get(`${API_URL}/api/bookings/my-bookings`),
        axios.get(`${API_URL}/api/favorites`),
        axios.get(`${API_URL}/api/verification/status`)
      ]);
      setBookings(bookingsRes.data);
      setFavorites(favoritesRes.data);
      setVerification(verificationRes.data);
    } catch (error) {
      logger.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleVerifySubmit = async () => {
    if (!verifyForm.id_type || (!verifyForm.id_number && !verifyForm.provide_later)) {
      toast.error('Please fill in the required fields');
      return;
    }
    setVerifyLoading(true);
    try {
      await axios.post(`${API_URL}/api/verification/submit`, verifyForm);
      toast.success(verifyForm.provide_later ? 'ID verification deferred for later' : 'ID submitted for verification');
      setShowVerifyModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to submit verification');
    } finally {
      setVerifyLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-50 text-green-700';
      case 'pending': return 'bg-amber-50 text-amber-700';
      case 'cancelled': return 'bg-red-50 text-red-700';
      default: return 'bg-slate-50 text-slate-600';
    }
  };

  const getVerificationBadge = () => {
    if (!verification || verification.status === 'not_submitted') {
      return (
        <button
          onClick={() => setShowVerifyModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 text-sm font-medium hover:bg-amber-100 transition-colors"
          data-testid="verify-id-btn"
        >
          <AlertCircle className="h-4 w-4" />
          Verify Your ID
        </button>
      );
    }
    if (verification.status === 'verified') {
      return (
        <div className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 text-sm font-medium">
          <CheckCircle2 className="h-4 w-4" />
          ID Verified
        </div>
      );
    }
    if (verification.status === 'deferred') {
      return (
        <button
          onClick={() => setShowVerifyModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-slate-50 text-slate-600 text-sm font-medium hover:bg-slate-100 transition-colors"
          data-testid="complete-verification-btn"
        >
          <Upload className="h-4 w-4" />
          Complete Verification
        </button>
      );
    }
    return (
      <div className="flex items-center gap-2 px-4 py-2 bg-orange-50 text-[#E07A3A] text-sm font-medium">
        <Clock className="h-4 w-4" />
        Verification Pending
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0C] premium-theme">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="w-8 h-8 border-2 border-[#E07A3A] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="buyer-dashboard">
      <Header />
      <RoleGuideBanner currentSection="/buyer" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* CLEAN HEADER — first thing a buyer sees on login.
            Was: 9 stacked promo banners pushing the actual portfolio
            below the fold. Now: hero + tabs + content. All upsells
            (Pulse Report, Pulse Premium, Affiliate, Local Pros, etc.)
            moved into a single collapsible "Tools & Offers" drawer
            below the portfolio. */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-2">
          <div>
            <span className="text-xs tracking-[0.3em] text-[#E07A3A] mb-1 block uppercase font-medium">
              {isTenant ? 'Tenant Portal' : isInvestor ? 'Investor / NRI Portal' : 'Buyer Portal'}
            </span>
            <h1 className="font-['Playfair_Display'] text-2xl sm:text-4xl text-[#1A1A1A]">
              {isTenant ? 'My Rental Search' : isInvestor ? 'My Investment Search' : 'My Portfolio'}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}.{' '}
              {isInvestor ? 'Yield-first listings across UK · India · Canada.' : "Here's what's happening with your search."}
            </p>
          </div>
          {getVerificationBadge()}
        </div>

        {/* Instant push alerts opt-in — drives engagement on new matches +
            restock relists. Self-contained component; renders an outline
            CTA → enabled state once subscribed. */}
        <div className="mt-3 mb-1 flex items-center gap-3 flex-wrap" data-testid="buyer-push-cta-row">
          <EnableMatchPushButton variant="compact" />
          <span className="text-xs text-slate-400">
            We'll ping you the moment a match lands or a saved listing relists.
          </span>
        </div>

        {/* PRIMARY CTA — Search Property. Buyers/Investors land here to search;
            this button is the first action they should be able to take. */}
        <Link
          to={isTenant ? '/properties?listing_type=rent' : '/properties'}
          data-testid="buyer-search-property-hero"
        >
          <div className="mt-5 mb-2 flex flex-wrap items-center gap-4 bg-gradient-to-r from-[#FEF8EC] via-white to-[#FEF3EC] text-[#0F172A] px-5 py-4 border border-[#E07A3A]/40 hover:border-[#E07A3A] transition-colors group rounded">
            <div className="w-10 h-10 bg-[#E07A3A]/10 border border-[#E07A3A]/30 flex items-center justify-center flex-shrink-0">
              {isInvestor ? <TrendingUp className="h-5 w-5 text-[#E07A3A]" /> : <SearchIcon className="h-5 w-5 text-[#E07A3A]" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-[#E07A3A] tracking-[0.2em] uppercase font-bold mb-0.5">Search property</p>
              <p className="text-sm font-semibold text-[#0F172A] truncate">
                {isInvestor
                  ? 'Browse yield-rated listings across UK · India · Canada — NRI-aware'
                  : isTenant
                    ? 'Browse rentals direct from verified landlords'
                    : 'Browse direct-from-seller listings — no agent commissions'}
              </p>
            </div>
            <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-white bg-[#E07A3A] px-3 py-1.5 group-hover:bg-[#C4652E] transition-colors flex-shrink-0">
              {isTenant ? 'Browse Rentals →' : 'Browse Properties →'}
            </span>
          </div>
        </Link>

        {/* Tabs — primary navigation, always visible */}
        <div className="mt-6">
          <DashboardTabs activeTab={activeTab} onTabChange={setActiveTab} tabs={buyerTabs} />
        </div>

        {/* 50%-off recovery banner appears only on abandoned-cart users —
            already self-hiding, safe to keep above the fold. */}
        <RecoveryDiscountBanner dataTestId="buyer-recovery-banner" />

        {/* ─── Primary content area: the tabs the user actually came for ─── */}

        {activeTab === 'professional' && <ProfessionalAdvisor userRole="buyer" />}
        {activeTab === 'find-agent' && <AgentMatcher />}
        {activeTab === 'govt-links' && <GovtLinksPanel country={country || 'UK'} />}
        {activeTab === 'agent-choice' && (
          <AgentChoicePanel
            userRole="buyer"
            onChooseAgent={() => setActiveTab('find-pros')}
            onChooseWithout={() => setActiveTab('journey')}
          />
        )}
        {activeTab === 'journey' && <BuyerJourney userRole={isTenant ? 'tenant' : 'buyer'} />}
        {activeTab === 'find-pros' && <FindProfessionals userRole="buyer" />}
        {activeTab === 'consultations' && <BuyerConsultations />}
        {activeTab === 'saved-searches' && <SavedSearchesTab />}
        {activeTab === 'deals' && (
          <div className="space-y-6">
            <DealTracker userRole="buyer" />
            <div className="luxury-card p-6">
              <h3 className="font-['Playfair_Display'] text-lg text-[#1A1A1A] mb-4">Market Pricing Trends</h3>
              <MarketTrends />
            </div>
          </div>
        )}

        {activeTab === 'portfolio' && (
        <>
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-12">
          <div className="luxury-card p-6 text-center">
            <p className="text-3xl font-['Playfair_Display'] text-[#E07A3A]">{favorites.length}</p>
            <p className="text-slate-500 text-sm mt-1">{isTenant ? 'Shortlisted Rentals' : 'Saved Properties'}</p>
          </div>
          <div className="luxury-card p-6 text-center">
            <p className="text-3xl font-['Playfair_Display'] text-[#E07A3A]">{bookings.length}</p>
            <p className="text-slate-500 text-sm mt-1">Viewings Booked</p>
          </div>
          <div className="luxury-card p-6 text-center">
            <p className="text-3xl font-['Playfair_Display'] text-[#E07A3A]">
              {bookings.filter(b => b.status === 'confirmed').length}
            </p>
            <p className="text-slate-500 text-sm mt-1">Confirmed</p>
          </div>
        </div>

        {/* PRIMARY: Saved Properties — what the tab name actually promises. */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A]">
              {isTenant ? 'Shortlisted Rentals' : 'Saved Properties'}
            </h2>
            <Link to="/properties">
              <Button variant="outline" className="text-xs uppercase tracking-wider" data-testid="browse-more-properties-btn">
                Browse all
              </Button>
            </Link>
          </div>
          {favorites.length > 0 ? (
            <div className="property-grid">
              {favorites.map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </div>
          ) : (
            <div className="luxury-card p-10 text-center" data-testid="empty-saved-properties">
              <Shield className="h-12 w-12 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-500 mb-2">No saved properties yet</p>
              <p className="text-slate-400 text-xs mb-4">Tap the heart icon on any property to save it here</p>
              <Link to="/properties">
                <Button className="bg-[#E07A3A] text-white hover:bg-[#C4652E]" data-testid="empty-browse-properties-btn">
                  Browse Properties
                </Button>
              </Link>
            </div>
          )}
        </div>

        {/* SECONDARY: Viewings — bookings the buyer has scheduled. */}
        <div>
          <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-6">My Viewings</h2>
          {bookings.length > 0 ? (
            <div className="space-y-4">
              {bookings.map((booking) => (
                <div key={booking.id} className="luxury-card p-5 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div className="flex-1">
                    <h3 className="text-[#1A1A1A] font-medium text-lg">{booking.property_title || 'Property'}</h3>
                    <div className="flex flex-wrap gap-4 mt-2 text-slate-500 text-sm">
                      <span className="flex items-center gap-1">
                        <CalendarIcon className="h-4 w-4" />
                        {booking.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {booking.time}
                      </span>
                      {booking.property_address && (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-4 w-4" />
                          {booking.property_address}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className={`px-4 py-1.5 text-xs tracking-wider uppercase font-medium ${getStatusColor(booking.status)}`}>
                    {booking.status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="luxury-card p-10 text-center">
              <CalendarIcon className="h-12 w-12 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-500">No viewings booked yet</p>
              <Link to="/properties">
                <Button className="mt-4 bg-[#E07A3A] text-white hover:bg-[#C4652E]" data-testid="browse-properties-btn">
                  Browse Properties
                </Button>
              </Link>
            </div>
          )}
        </div>
        </>
        )}

        {/* Help & Guide — collapsible, at bottom */}
        <details className="mt-8" data-testid="buyer-help-guide">
          <summary className="cursor-pointer flex items-center gap-2 text-sm text-[#E07A3A] font-semibold tracking-wider uppercase hover:text-[#C4652E] transition-colors py-3">
            <HelpCircle className="h-4 w-4" />
            Help & Getting Started Guide
          </summary>
          <div className="mt-3">
            <GettingStartedGuide role="buyer" />
          </div>
        </details>

        {/* ─── COLLAPSIBLE "TOOLS & OFFERS" — moved here from the top so a
              new buyer sees their dashboard first, not a wall of upsells.
              Default open so nothing is hidden, just relocated. ─── */}
        <details
          className="mt-10 border border-slate-200 rounded-lg bg-white overflow-hidden"
          open
          data-testid="buyer-tools-offers"
        >
          <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between bg-gradient-to-r from-[#FEF8EC] via-white to-[#FEF3EC] border-b border-slate-100 hover:bg-amber-50/40 transition-colors">
            <div className="flex items-center gap-3">
              <Activity className="h-5 w-5 text-[#E07A3A]" />
              <div>
                <p className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E07A3A]">Tools &amp; Offers</p>
                <p className="text-sm font-semibold text-[#0F172A]">Reports, subscriptions, pros &amp; earnings · click to collapse</p>
              </div>
            </div>
            <span className="text-[10px] text-slate-500 hidden sm:block">All optional</span>
          </summary>

          <div className="p-5 space-y-5">
            {/* Pulse Investment Report £49 */}
            <Link
              to={`/free-valuation?country=${(user?.country || country || 'UK').toUpperCase()}&ref=buyer-dashboard`}
              className="flex flex-wrap items-center gap-4 bg-gradient-to-r from-[#FEF8EC] via-white to-[#FEF3EC] text-[#0F172A] px-5 py-4 border border-[#E07A3A]/40 hover:border-[#E07A3A] transition-colors group shadow-sm rounded"
              data-testid="buyer-pulse-sell-cta"
            >
              <div className="w-10 h-10 bg-[#E07A3A]/10 border border-[#E07A3A]/30 flex items-center justify-center flex-shrink-0">
                <Activity className="h-5 w-5 text-[#E07A3A]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-[#E07A3A] tracking-[0.2em] uppercase font-bold mb-0.5">
                  Research Before You Bid · £49 / ₹4,900 / C$79
                </p>
                <p className="text-sm font-semibold text-[#0F172A] truncate">
                  Independent Pulse Investment Report — median prices, comps &amp; 12-month outlook
                </p>
              </div>
              <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-white bg-[#E07A3A] px-3 py-1.5 group-hover:bg-[#C4652E] transition-colors flex-shrink-0" data-testid="buyer-pulse-sell-cta-buy-btn">
                Buy Report
              </span>
              <ArrowRight className="h-4 w-4 text-[#E07A3A] flex-shrink-0 group-hover:translate-x-1 transition-transform" />
            </Link>

            <BuyerPulseSubscribeCard country={(user?.country || country || 'UK').toUpperCase()} dataTestId="buyer-dashboard-pulse-card" />

            <LocalProsPanel
              role={user?.is_nri ? 'nri' : 'buyer'}
              city={user?.preferred_city || user?.city || ''}
              country={(user?.country || country || 'UK').toUpperCase()}
              dataTestId="buyer-local-pros-panel"
            />

            <FindProfessionalPromo compact country={user?.country || 'UK'} dataTestId="buyer-find-pro-promo" />

            <SubscriptionsAndFeesCard country={(user?.country || country || 'UK').toUpperCase()} role={isTenant ? 'tenant' : 'buyer'} dataTestId="buyer-fees-card" />

            <ComplianceBanner />

            <InviteTeaserCard />

            <LatestBlogStrip dataTestId="buyer-latest-blog" />

            <AffiliateWidget dataTestId="buyer-affiliate-widget" />
          </div>
        </details>
      </div>

      {/* Verification Modal */}
      <Dialog open={showVerifyModal} onOpenChange={setShowVerifyModal}>
        <DialogContent className="bg-white border-slate-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1A1A1A]">
              <FileCheck className="h-5 w-5 text-[#E07A3A]" />
              ID Verification
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Submit one valid ID for verification. This must match property-related documents.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-700 font-medium">ID Type</Label>
              <Select value={verifyForm.id_type} onValueChange={(v) => setVerifyForm({ ...verifyForm, id_type: v })}>
                <SelectTrigger className="bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] h-12" data-testid="id-type-select">
                  <SelectValue placeholder="Select ID Type" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="passport">Passport</SelectItem>
                  <SelectItem value="driving_license">Driving Licence</SelectItem>
                  <SelectItem value="national_id">National ID Card</SelectItem>
                  <SelectItem value="residence_permit">Residence Permit</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-slate-700 font-medium">ID Number</Label>
              <Input
                value={verifyForm.id_number}
                onChange={(e) => setVerifyForm({ ...verifyForm, id_number: e.target.value })}
                placeholder="Enter your ID number"
                className="bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] h-12"
                disabled={verifyForm.provide_later}
                data-testid="id-number-input"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="provide-later"
                checked={verifyForm.provide_later}
                onChange={(e) => setVerifyForm({ ...verifyForm, provide_later: e.target.checked })}
                className="rounded border-slate-300"
                data-testid="provide-later-checkbox"
              />
              <Label htmlFor="provide-later" className="text-slate-600 text-sm cursor-pointer">
                I'll provide ID documents later at the deal stage
              </Label>
            </div>

            <Button
              onClick={handleVerifySubmit}
              disabled={verifyLoading}
              className="w-full bg-[#E07A3A] text-white hover:bg-[#C4652E] h-12"
              data-testid="submit-verification-btn"
            >
              {verifyLoading ? 'Submitting...' : verifyForm.provide_later ? 'Submit (Deferred)' : 'Submit for Verification'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BuyerDashboard;
